<template>
  <div style="margin-top: 20px">
    <FiPageRail title="Bus SOS Dashboard" nowrapActions>
      <!-- yard-only provider chips (the sub slot renders under both the horizon rail and classic) -->
      <template v-if="yard_only" #sub>
        <span class="dash-chip">Yard-only provider</span>
      </template>
      <template #actions>
        <b-button size="sm" variant="outline-secondary" @click="showSettings = true"
                  title="Bus SOS settings" aria-label="Bus SOS settings">
          <i class="fa fa-cog"></i>&nbsp;Settings
        </b-button>
        <b-button size="sm" variant="outline-secondary" @click="showInfo = true" title="About this page" aria-label="About this page">
          <i class="fa fa-info-circle"></i>
        </b-button>
      </template>
    </FiPageRail>

    <sos-settings-modal v-model="showSettings" @showAlert="forward_alert" @saved="load" />

    <b-modal v-model="showInfo" title="About Bus SOS" ok-only ok-title="Close" size="lg" scrollable>
      <p><strong>Bus SOS</strong> is a mutual-aid tool for a disabled bus: it gets the right people the vehicle, driver and location fast — your own dispatch first, and nearby partner companies only if you choose to escalate.</p>

      <h6 class="mt-3">Three ways to raise an SOS</h6>
      <ol class="mb-0 pl-3">
        <li class="mb-1"><strong>Public yard map</strong> (<code>/sos</code>) — no login. Anyone drops a pin, sets a radius, and sees nearby bus yards with phone numbers to call. Nothing is filed — it's a find-help-nearby lookup.</li>
        <li v-if="yard_only" class="mb-1"><strong>Driver SOS page</strong> — your login-free link for drivers. A driver drops a pin (or uses their location / types an address) and says what's wrong; the pin is the SOS location and your dispatch is alerted by text/email. Your company has no vehicle tracking, so the bus number is only a label.</li>
        <li v-else class="mb-1"><strong>Driver SOS page</strong> — your login-free link for drivers. A driver enters their bus number (or types an address / drops a pin) and what's wrong; the system resolves the vehicle, driver and live location and alerts your dispatch by text/email.</li>
        <li><strong>Dispatcher page</strong> (Send SOS) — your staff raise an SOS on a driver's behalf: set the location, vehicle and driver, then send.</li>
      </ol>

      <h6 class="mt-3">Text alerts &amp; phone numbers</h6>
      <ul class="mb-0 pl-3">
        <li v-if="yard_only" class="mb-1"><strong>Your account (yard-only)</strong> shares <strong>one</strong> Bus SOS phone number with the other yard-only providers — used only to <em>send</em> alerts to your dispatch and drivers, so there's no per-company number to set up. It <strong>doesn't accept SOS by text</strong>: a text to it just replies pointing the driver to your driver link. Drivers raise an SOS from the <strong>driver link</strong>; staff use <strong>Send SOS</strong>.</li>
        <li v-else class="mb-1"><strong>Your account</strong> has its own <strong>dedicated Bus SOS number</strong>. Drivers can text <strong>SOS &lt;bus #&gt;</strong> (or share a map pin) to it to raise an SOS, and the shared driver/dispatch thread runs through it.</li>
        <li>In short: <strong>yard-only</strong> providers share one number and can't take SOS by text; <strong>GPS / telematics</strong> and <strong>Coach Manager (CM) / TBN</strong> accounts each get their own dedicated number.</li>
      </ul>

      <h6 class="mt-3">The lifecycle of an SOS</h6>
      <ol class="mb-0 pl-3">
        <li class="mb-1"><strong>Raised (open).</strong> The SOS is created and your dispatch is notified. The driver gets a tracking link to follow status and add updates.</li>
        <li class="mb-1"><strong>Internal first.</strong> By default it stays inside your company — your dispatch, your nearby vehicles and your yards. The dispatcher view shows the bus, your fleet nearby, and your yards.</li>
        <li class="mb-1"><strong>Escalate → broadcast.</strong> Need outside help? The dispatcher broadcasts the SOS to opted-in partner companies with a yard within the search radius. Each partner gets its own copy with the location, vehicle and the driver's callback number.</li>
        <li class="mb-1"><strong>Coordinate.</strong> The driver and your dispatcher talk through a shared thread — each message is also texted to the other. A partner that can help calls the operator directly.</li>
        <li><strong>Respond &amp; resolve.</strong> Partners can mark a response (acknowledged / en route / arrived / can't help). When help is arranged — or the situation clears — mark the SOS <strong>Resolved</strong>, or <strong>Cancel</strong> it. A "stand down" update can be re-broadcast to the same group.</li>
      </ol>

      <h6 class="mt-3">How escalation (broadcast) works</h6>
      <ul class="mb-0 pl-3">
        <li class="mb-1">It reaches only opted-in partners <strong>with a yard in range</strong> of the pin — never every company platform-wide. You see those companies by name.</li>
        <li class="mb-1">Partners whose only presence in range is a live <strong>vehicle</strong> are reached too, but shown to you as an anonymous count — their vehicle positions stay private.</li>
        <li>Both sides opt in: you enable "send to other companies," they enable "receive from other companies."</li>
      </ul>

      <h6 class="mt-3">This dashboard</h6>
      <p class="mb-0">Shows your network reach and yard counts. Use the <strong>Bus SOS menu</strong> for Send SOS, the map, and full Sent/Received history; the gear opens Bus SOS settings (your link, recipients, opt-in flags and yards).</p>
    </b-modal>

    <div class="dash">
      <div v-if="loading" class="dash-loading">
        <b-spinner></b-spinner>
      </div>

      <template v-else>
        <!-- network + yard stats, grouped separately -->
        <div class="dash-stats">
          <div class="dash-stat-group">
            <div class="dash-section-title">Network</div>
            <div class="dash-tiles dash-tiles--2">
              <div class="dash-tile">
                <div class="dash-tile-num">{{ receiversCount }}</div>
                <div class="dash-tile-label">Companies you can alert</div>
              </div>
              <div class="dash-tile">
                <div class="dash-tile-num">{{ sendersCount }}</div>
                <div class="dash-tile-label">Companies that can alert you</div>
              </div>
            </div>
          </div>

          <div class="dash-stat-group">
            <div class="dash-section-title">Yards</div>
            <div class="dash-tiles dash-tiles--2">
              <div class="dash-tile">
                <div class="dash-tile-num">{{ ownYards }}</div>
                <div class="dash-tile-label">Your yards</div>
              </div>
              <div class="dash-tile">
                <div class="dash-tile-num">{{ networkYards }}</div>
                <div class="dash-tile-label">Network yards</div>
              </div>
            </div>
          </div>
        </div>

        <!-- the three ways into Bus SOS, each with a little map/phone illustration -->
        <div class="dash-section-title">How to raise an SOS</div>
        <div class="dash-tiers">
          <div class="dash-tier">
            <div class="dash-tier-art">
              <svg class="dash-tier-svg" viewBox="0 0 260 104" role="img" aria-label="A map showing your yards and nearby partner yards">
                <rect x="4" y="4" width="252" height="96" rx="10" fill="#eef3f7" stroke="#dbe4ec"/>
                <path d="M4 66 H256 M120 4 V100 M44 4 L96 100" stroke="#dbe4ec" stroke-width="2" fill="none"/>
                <circle cx="130" cy="52" r="40" fill="#23608f" fill-opacity="0.06" stroke="#23608f" stroke-opacity="0.4" stroke-width="1.5" stroke-dasharray="4 4"/>
                <g fill="#23608f"><circle cx="86" cy="38" r="5"/><circle cx="178" cy="46" r="5"/><circle cx="152" cy="82" r="5"/><circle cx="96" cy="78" r="5"/></g>
                <path d="M130 62 L123 48 L137 48 Z" fill="#c0392b"/>
                <circle cx="130" cy="44" r="9" fill="#c0392b"/><circle cx="130" cy="44" r="3.6" fill="#fff"/>
              </svg>
            </div>
            <div class="dash-tier-head"><span class="dash-tier-badge">1</span><i class="fa fa-map-o"></i>&nbsp;SOS Yard Map</div>
            <p class="dash-tier-desc">A live map of your vehicles, your yards, other Fleet Integrations customers' yards, and association yards. Toggle layers, search the shown list to zoom, and get each yard's contact info &mdash; so you know who's nearby before an SOS.</p>
            <router-link class="dash-tier-link" :to="{ name: 'sos_map_page' }">Open the SOS Yard Map&nbsp;<i class="fa fa-arrow-right"></i></router-link>
          </div>
          <div class="dash-tier">
            <div class="dash-tier-art">
              <svg class="dash-tier-svg" viewBox="0 0 260 104" role="img" aria-label="A phone dropping a location pin, with a sent confirmation">
                <rect x="4" y="4" width="252" height="96" rx="10" fill="#f4f7fa"/>
                <rect x="98" y="9" width="64" height="86" rx="10" fill="#fff" stroke="#cdd6de" stroke-width="1.5"/>
                <rect x="119" y="14" width="22" height="3" rx="1.5" fill="#dbe4ec"/>
                <rect x="104" y="23" width="52" height="45" rx="4" fill="#eef3f7"/>
                <path d="M104 50 H156 M130 23 V68" stroke="#dbe4ec" stroke-width="1.5"/>
                <path d="M130 56 L124 45 L136 45 Z" fill="#c0392b"/>
                <circle cx="130" cy="41" r="7.5" fill="#c0392b"/><circle cx="130" cy="41" r="3" fill="#fff"/>
                <rect x="106" y="75" width="48" height="14" rx="7" fill="#e3f0e7" stroke="#2f7d4f" stroke-opacity="0.4"/>
                <path d="M115 82 l3 3 l5 -6" fill="none" stroke="#2f7d4f" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <text x="130" y="86" font-size="9" fill="#2f7d4f" font-weight="700">Sent</text>
              </svg>
            </div>
            <div class="dash-tier-head"><span class="dash-tier-badge">2</span><i class="fa fa-bus"></i>&nbsp;Driver SOS page</div>
            <p v-if="yard_only" class="dash-tier-desc">Your login-free link to share with drivers. They drop a pin (or use their location / type an address), add what's wrong, and send &mdash; it alerts your dispatch and starts an SOS.</p>
            <p v-else class="dash-tier-desc">Your login-free link to share with drivers. They enter their bus number (or type an address / drop a pin), add what's wrong, and send &mdash; it alerts your dispatch and starts an SOS.</p>
            <a v-if="driverUrl" class="dash-tier-link" :href="driverUrl" target="_blank" rel="noopener">Open the driver page&nbsp;<i class="fa fa-external-link"></i></a>
            <span v-else class="dash-tier-nolink">Set your Bus SOS link in <a href="#" @click.prevent="showSettings = true">Settings</a> to enable this.</span>
            <!-- only a dedicated (telematics/GPS/CM/TBN) number takes SOS by text; the shared
                 yard-only number does not, so drivers there must use the link above -->
            <div v-if="smsNumber && !yard_only" class="dash-tier-sms">
              <i class="fa fa-commenting-o"></i>&nbsp;Or drivers text <strong>SOS</strong> + bus # to
              <a :href="smsNumberLink" class="dash-sms-link">{{ smsNumber }}</a>
            </div>
            <div v-else-if="yard_only" class="dash-tier-sms">
              <i class="fa fa-info-circle"></i>&nbsp;This account doesn't take SOS by text — drivers use the link above.
            </div>
          </div>
          <div class="dash-tier">
            <div class="dash-tier-art">
              <svg class="dash-tier-svg" viewBox="0 0 260 104" role="img" aria-label="An SOS broadcast to partner yards within a radius">
                <rect x="4" y="4" width="252" height="96" rx="10" fill="#eef3f7" stroke="#dbe4ec"/>
                <circle cx="130" cy="54" r="24" fill="#23608f" fill-opacity="0.05" stroke="#23608f" stroke-opacity="0.5" stroke-width="1.5" stroke-dasharray="4 4"/>
                <circle cx="130" cy="54" r="42" fill="none" stroke="#23608f" stroke-opacity="0.3" stroke-width="1.5" stroke-dasharray="4 4"/>
                <g fill="#23608f"><circle cx="88" cy="38" r="5"/><circle cx="178" cy="42" r="5"/><circle cx="164" cy="86" r="5"/></g>
                <path d="M130 62 L123 48 L137 48 Z" fill="#c0392b"/>
                <circle cx="130" cy="44" r="9" fill="#c0392b"/><circle cx="130" cy="44" r="3.6" fill="#fff"/>
              </svg>
            </div>
            <div class="dash-tier-head"><span class="dash-tier-badge">3</span><i class="fa fa-paper-plane"></i>&nbsp;Dispatcher SOS page</div>
            <p v-if="yard_only" class="dash-tier-desc">For your staff. Raise an SOS on a driver's behalf: drop a pin for the location, add the vehicle and driver as labels, then send it internally or broadcast to opted-in partner companies with a yard in range.</p>
            <p v-else class="dash-tier-desc">For your staff. Raise an SOS on a driver's behalf: set the location, vehicle and driver, then send it internally or broadcast to opted-in partner companies with a yard in range.</p>
            <router-link class="dash-tier-link" :to="{ name: 'sos_send' }">Open Send SOS&nbsp;<i class="fa fa-arrow-right"></i></router-link>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { get_sos_history, get_sos_overlay, get_sos_settings } from '../networking/apis'
import { mapState } from 'vuex'
import moment from 'moment'
import SosSettingsModal from './sos_settings_modal.vue'

export default {
  name: 'sos_dashboard',
  components: { SosSettingsModal },
  data() {
    return {
      loading: true,
      showInfo: false,
      showSettings: false,
      summary: {},
      network_yards: 0,
      own_yards: 0,
      slug: null,   // this tenant's Bus SOS link slug, for the driver-page URL
      // yard-only provider flag from get_sos_settings (new field; an older API omits it)
      yard_only: false,
      sms_number: null,   // the inbound number a driver texts "SOS <bus>" to
    }
  },
  computed: {
    ...mapState({
      user: state => state.profile.user,
    }),
    sendersCount() {
      return (this.summary.sos_request_senders || []).length
    },
    receiversCount() {
      return (this.summary.sos_request_receivers || []).length
    },
    networkYards() {
      return this.network_yards
    },
    ownYards() {
      return this.own_yards
    },
    driverUrl() {
      // the tenant's login-free driver form; needs a configured Bus SOS link (slug)
      return this.slug ? window.location.origin + '/#/sos/' + this.slug : null
    },
    smsDigits() {
      return (this.sms_number || '').replace(/\D/g, '')
    },
    smsNumber() {
      // pretty-print the text-to number, e.g. "19285636250" -> "(928) 563-6250"
      let d = this.smsDigits
      if (d.length === 11 && d[0] === '1') d = d.slice(1)
      if (d.length === 10) return '(' + d.slice(0, 3) + ') ' + d.slice(3, 6) + '-' + d.slice(6)
      return this.sms_number || ''
    },
    smsNumberLink() {
      const d = this.smsDigits
      if (!d) return null
      return 'sms:+' + (d.length === 10 ? '1' + d : d)
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    forward_alert(...args) {
      this.$emit('showAlert', ...args)
    },
    async load() {
      this.loading = true
      const from = moment().add(-30, 'days').format('YYYY-MM-DD')
      const to = moment().format('YYYY-MM-DD')
      try {
        const { value: { data = {} } } = await get_sos_history(this.user.tenant, from, to)
        this.summary = data.summary || {}
        if (this.summary.timezone) moment.tz.setDefault(this.summary.timezone)
      } catch (e) {
        this.summary = {}
      }
      try {
        const { value: { data = {} } } = await get_sos_overlay(this.user.tenant)
        this.network_yards = (data.network_yards || []).length
        this.own_yards = (data.own_yards || []).length
      } catch (e) {
        this.network_yards = 0
        this.own_yards = 0
      }
      try {
        const { value: { data = {} } } = await get_sos_settings(this.user.tenant)
        this.slug = data.slug || null
        this.yard_only = data.yard_only === true
        this.sms_number = data.sms_number || null
      } catch (e) {
        this.slug = null
        this.yard_only = false
      }
      this.loading = false
    },
  },
}
</script>

<style scoped>
.dash {
  max-width: 860px;
  margin: 0 auto;
  padding: 16px;
}
.dash-loading {
  text-align: center;
  padding: 40px;
}
.dash-open {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: #f6e4e0;
  border: 1px solid #e7c3bb;
  border-radius: 10px;
  padding: 14px 18px;
  margin-bottom: 16px;
}
.dash-open-left {
  display: flex;
  align-items: center;
  gap: 10px;
}
.dash-open-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #c0392b;
  animation: dash-pulse 1.6s ease-in-out infinite;
}
@media (prefers-reduced-motion: reduce) {
  .dash-open-dot { animation: none; }
}
@keyframes dash-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.35; }
}
.dash-open-count {
  font-size: 22px;
  font-weight: 700;
  color: #ae3f2e;
}
.dash-open-text {
  font-weight: 600;
  color: #7d3225;
}
.dash-clear {
  background: #e3f0e7;
  border: 1px solid #c6e0d0;
  border-radius: 10px;
  padding: 12px 18px;
  margin-bottom: 16px;
  color: #2f7d4f;
  font-weight: 600;
}
.dash-clear-check {
  font-weight: 700;
  margin-right: 6px;
}
/* yard-only provider chip under the title */
.dash-chip {
  display: inline-block;
  padding: 2px 9px;
  margin-right: 6px;
  border-radius: 999px;
  background: #e8eef5;
  color: #23608f;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
}
.dash-stats {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;
}
.dash-stat-group .dash-section-title {
  margin-bottom: 8px;
}
.dash-tiles {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 16px;
}
.dash-tiles--2 {
  grid-template-columns: repeat(2, 1fr);
  margin-bottom: 0;
}
.dash-tile {
  background: #fff;
  border: 1px solid #e6e9ec;
  border-radius: 10px;
  padding: 16px;
  text-align: center;
}
.dash-tile-num {
  font-size: 30px;
  font-weight: 700;
  color: #23608f;
  font-variant-numeric: tabular-nums;
  line-height: 1.1;
}
.dash-tile-label {
  font-size: 13px;
  color: #6e7e8f;
  margin-top: 4px;
}
.dash-section-title {
  font-size: 15px;
  font-weight: 700;
  color: #1c2733;
  margin-bottom: 8px;
}
.dash-tiers {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 20px;
}
.dash-tier {
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid #e6e9ec;
  border-radius: 10px;
  padding: 14px 16px;
}
.dash-tier-head {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 700;
  color: #1c2733;
  margin-bottom: 6px;
}
.dash-tier-badge {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #23608f;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  margin-right: 2px;
}
.dash-tier-desc {
  font-size: 13px;
  color: #55636f;
  line-height: 1.5;
  margin: 0 0 12px;
  flex: 1 1 auto;
}
.dash-tier-link {
  align-self: flex-start;
  font-size: 13px;
  font-weight: 600;
  color: #23608f;
  text-decoration: none;
}
.dash-tier-link:hover {
  text-decoration: underline;
}
.dash-tier-nolink {
  font-size: 13px;
  color: #8295a7;
}
.dash-tier-sms {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #eef1f4;
  font-size: 13px;
  color: #55636f;
}
.dash-sms-link {
  font-weight: 700;
  color: #23608f;
  text-decoration: none;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.dash-sms-link:hover {
  text-decoration: underline;
}
/* the little map/phone illustration atop each "how to" tier - bleeds to the card edges */
.dash-tier-art {
  margin: -14px -16px 12px;
  padding: 10px 10px 0;
  background: #f8fafc;
  border-bottom: 1px solid #eef1f4;
  border-radius: 10px 10px 0 0;
}
.dash-tier-svg {
  display: block;
  width: 100%;
  height: auto;
}
@media (max-width: 760px) {
  .dash-tiers {
    grid-template-columns: 1fr;
  }
}
@media (max-width: 640px) {
  .dash-stats {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  .dash-tiles {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
